import { useEffect, useState } from 'react';
import { publicApi } from '@/config/axios.config';

/** 'unwired' = el proveedor no tiene ningun camino de ejecucion (no es lo mismo que 'mock'). */
export type ProviderMode = 'live' | 'mock' | 'unwired';

export interface ProviderStatus {
  stripe: ProviderMode;
  /**
   * PayPal de plataforma (GH#887). Opcional hasta que el API lo publique (GH#1748 / #2066):
   * un payload sin `paypal` se lee como «no live», que es lo que de verdad pasa hoy.
   */
  paypal?: ProviderMode;
  alanube: ProviderMode;
  idecus: ProviderMode;
  twilio: ProviderMode;
  whatsapp: ProviderMode;
  anyMock: boolean;
  mockProviders: string[];
  environment: string;
  timestamp: string;
}

interface UseProviderStatusResult {
  status: ProviderStatus | null;
  loading: boolean;
  error: string | null;
}

/**
 * gap-mock-mode-banner — fetches provider mock-vs-live state from
 * GET /api/v1/system/provider-status. Drives the persistent aviso banner
 * so usuario nunca confunde un cobro o NCF simulado con uno real.
 *
 * Uses publicApi (endpoint is @Public on purpose — banner must render
 * before login too). Los EXITOS se cachean en un singleton para que el aviso
 * rinda sin parpadeo en montajes siguientes.
 *
 * GH#887 — si la consulta FALLA no se inventa un estado: `status` queda null y
 * `error` trae el mensaje. Antes se armaba un payload con todo 'mock' y el aviso
 * afirmaba «simulado» sin saberlo; ahora el banner dice que no pudo verificar.
 * Un fallo NO se cachea: el proximo montaje vuelve a preguntar.
 */
let cached: ProviderStatus | null = null;

/** Solo para tests: el singleton sobrevive entre casos del mismo archivo. */
export const __resetProviderStatusCache = (): void => {
  cached = null;
};

export const useProviderStatus = (): UseProviderStatusResult => {
  const [status, setStatus] = useState<ProviderStatus | null>(cached);
  const [loading, setLoading] = useState<boolean>(!cached);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (cached) {
      // Already fetched once this page-load — banner is stable.
      return;
    }
    publicApi
      .get<ProviderStatus>('/system/provider-status')
      .then((res) => {
        if (cancelled) return;
        cached = res.data;
        setStatus(res.data);
        setError(null);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setStatus(null);
        setError(err?.message || 'provider-status fetch failed');
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return { status, loading, error };
};
